// Floor home — the hub the floor surfaces hang off: the five module-06 screens
// (spec 06 §4), module 07's inspection queue (07-A), and module 04's station
// entry + my-shift tally (S9/S11 — the operator-shift audit additions below).
//
// It exists because reachability is part of "complete": a screen with no way in
// is not shipped. It is also where the §7 permission mirror is applied ONCE for
// the whole area, so a floor_worker who cannot receive transfers does not tap a
// tile that leads to a guaranteed 403.
//
// The mirror governs CONTROLS only (the §4 preamble rule the office area
// records): the server is the authorisation boundary, and every tile's screen
// renders the server's own refusal if the mirror was wrong or the role is a
// custom one the client cannot resolve.
//
// ---------------------------------------------------------------------------
// FAIL-CLOSED IS RIGHT. SILENT IS NOT (D-118, UX-5 surface review finding 2).
//
// The mirror is fed by `useRoleCodesFeed`, which is network-bound and lives in
// react-query's IN-MEMORY cache. That cache does not survive a document
// boundary. Any tablet whose first floor screen is not `/floor` — a `/q/wc/`
// QR scan, a bookmarked station, a mounted tablet parked at its machine and
// reloaded — has never resolved the feed, and if it then walks into a dead
// corner the feed cannot resolve at all.
//
// Until this fix the hub read `perms.has(code)` alone, which is fail-closed and
// therefore answered NO to all three of Scan, Receive and Stock count — and the
// tiles were simply ABSENT. Measured on a cold offline start: six tiles online,
// three tiles offline, no message. The three that vanish are precisely the
// offline-capable actions the outbox exists for, so the operator loses them in
// the one condition the whole offline design was built for, and the screen does
// not say that anything happened.
//
// The rule the hub now holds: an UNRESOLVED feed and a RESOLVED refusal are
// different facts and get different renderings. Resolved + not granted → the
// tile is gone, because the role really does not have it. Unresolved or failed
// → the tile STAYS, inert, saying why. "We cannot tell" is never rendered as
// "you are not permitted" (the rule role-codes.ts already states for read
// surfaces; this is the same rule for the hub's controls).
//
// ---------------------------------------------------------------------------
// AND AN INERT TILE MUST LOOK INERT AND ANSWER A TAP (task #115, 2026-08-20).
//
// The paragraph above shipped as a NON-CLICKABLE `<p>` wearing the same
// card-sized slab as its live neighbours, differing from them in a dashed
// border, a grey fill and one line of subtitle text. Under a work glove, at a
// metre, on a screen with copper dust on it, that is a live tile — and a tap on
// a `<p>` is a NO-OP THAT CANNOT FAIL: no ripple, no spinner, no sentence,
// nothing in the console, nothing in the outbox. The operator's model becomes
// "this app ignores me", which is the state the whole floor design exists to
// prevent, and the harness hit the same wall from the other side (a Playwright
// click that succeeded, navigated nowhere, and burnt a 60 s wait — M07-S08 red
// on staging, 2026-08-19; the harness half is fixed in e2e/, this is the
// product half).
//
// THE SHAPE, decided here for EVERY tile on the hub rather than for the one the
// walkthrough happened to click:
//
//   granted     `<Link data-grant="granted">` — unchanged, and the attribute is
//               a contract: the harness locates the navigable shape by it.
//   checking    the feed is in flight. The slab STAYS in place at the same
//               height, dashed / sunken / shadowless, its subtitle replaced by
//               "Checking what you can do here" led by the floor's own pending
//               bar (`.skeleton`, the shimmer `FloorSkeleton` paints) — the one
//               cue that survives arm's length is MOTION, and reusing the
//               floor's existing pending idiom rather than inventing a third
//               one is the point.
//   unreachable the feed has failed and stopped retrying. Same slab, no motion
//               (nothing is in flight — a shimmer there would be a lie), the
//               reason ("needs signal once") plus the fix offered AT the
//               control: tap to ask again.
//   refused     absent. Unchanged, and now pinned by a test.
//
// WHY A `<button aria-disabled>` AND NOT A DISABLED ONE. `disabled` swallows
// the tap as completely as the `<p>` did — the operator learns nothing from a
// control that eats the event. `aria-disabled` keeps the element focusable and
// clickable while announcing that it is not live, so the tap is ANSWERED: a
// warn buzz (`floorFeedback` — the beep/buzz vocabulary every Zebra handheld on
// the floor already trained the operator in), a sentence inside the slab, and,
// when the feed has given up, a real re-fetch of the role feed. D-118's other
// half in one shape: the reason a dead control gives is the one that unblocks
// it.
//
// WHAT IS DELIBERATELY NOT BUILT: a QUEUED tap that navigates by itself once
// the feed resolves. A hub tile leads to a screen with live controls on it, and
// a floor tablet that changes screens under a thumb one second after a tap is
// how a queued navigation turns into a mis-tap on the destination — booking a
// quantity, receiving the wrong transfer. The tile re-renders into its live
// `<Link>` the moment the answer lands, which the operator can see; the second
// tap is theirs.

import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AUTH_TENANTS_QUERY_KEY, type RoleSignal } from "../../../lib/role-codes";
import { useInventoryPermissions } from "../../office/inventory/hooks/permissions";
import { deriveQualityPermissions } from "../../office/quality/hooks/permissions";
// The SALES mirror — `apps/web/src/areas/office/sales/hooks/permissions.ts`,
// the second file called permissions.ts (the catalog is
// `packages/contracts/src/permissions.ts`). Read here, never edited here.
import { deriveSalesPermissions } from "../../office/sales/hooks/permissions";
import { useProductionPermissions } from "../../office/production/hooks/permissions";
import { useFloorOutbox } from "../hooks/use-floor-outbox";
import { floorFeedback } from "../lib/floor-feedback";
import { pinnedStation } from "../lib/production-floor";
import { FloorScreen } from "../components/floor-chrome";
import { guideHref } from "../../../lib/guide-links";

/**
 * What the mirror can say about one tile.
 *
 * The old `unknown` is split by CAUSE, because "we are asking" and "we asked
 * and could not reach anyone" need different sentences and different
 * affordances. Both still render `data-grant="unknown"` — the attribute is the
 * harness's contract for "not the navigable shape" and does not fork with the
 * copy.
 */
type TileGrant = "granted" | "refused" | "checking" | "unreachable";

function grantOf(
  perms: { isResolved: boolean; isFeedError: boolean; has: (code: string) => boolean },
  code: string,
): TileGrant {
  return tileGrant(perms, perms.has(code));
}

/** The shared branch, so the quality tile cannot drift from its neighbours. */
function tileGrant(
  perms: { isResolved: boolean; isFeedError: boolean },
  granted: boolean,
): TileGrant {
  // ORDER MATTERS. react-query keeps `isFetched` (→ `isResolved`) TRUE across a
  // retry of a query that has already failed, so the failure is asked about
  // first: a feed being re-fetched because the operator tapped must not
  // silently re-label itself "checking" and drop the reason they tapped for.
  if (perms.isFeedError) return "unreachable";
  if (!perms.isResolved) return "checking";
  return granted ? "granted" : "refused";
}

/**
 * A tile whose destination the mirror may or may not have an answer for.
 *
 * `checking` / `unreachable` render the same slab in the same place at the same
 * height — so the hub's layout is the same wall of tiles whether or not this
 * tablet has ever reached the identity service, and nothing moves under a thumb
 * when the answer lands — but as a visibly-not-live `<button>` that answers a
 * tap instead of a `<p>` that eats it. See the header block for the ruling.
 */
function GatedTile({
  grant,
  to,
  testId,
  title,
  sub,
}: {
  grant: TileGrant;
  to: "/floor/scan" | "/floor/receive" | "/floor/counts" | "/floor/quality" | "/floor/ship";
  testId: string;
  title: string;
  sub: ReactNode;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Per tile, and deliberately not shared: the operator asked about THIS
  // destination, and the answer belongs at the slab their thumb landed on.
  const [asked, setAsked] = useState(false);

  // D-118: the role really does not hold this code, so the control is ABSENT.
  // Not disabled, not greyed — a permanently dead tile on a hub of nine is
  // furniture that teaches the operator to distrust the other eight.
  if (grant === "refused") return null;

  if (grant === "granted") {
    return (
      <Link to={to} className="floor-tile" data-testid={testId} data-grant="granted">
        <span className="floor-tile-title">{title}</span>
        <span className="floor-tile-sub">{sub}</span>
      </Link>
    );
  }

  const checking = grant === "checking";
  return (
    <button
      type="button"
      className={`floor-tile floor-tile-unavailable${checking ? " floor-tile-checking" : ""}`}
      data-testid={testId}
      data-grant="unknown"
      /* The CAUSE, for the tests and for anyone debugging a tablet: `unknown`
         stays the harness's contract, this says which of the two it is. */
      data-tile-state={grant}
      /* NOT `disabled` — see the header. `aria-disabled` states "not live" to a
         screen reader while leaving the tap deliverable, which is the whole
         difference between a control that refuses and a control that ignores. */
      aria-disabled="true"
      {...(checking ? { "aria-busy": true } : {})}
      onClick={() => {
        setAsked(true);
        // The floor's own non-visual vocabulary (floor-feedback.ts): two pulses
        // = "something happened, read the screen". A gloved thumb at a machine
        // gets an answer before the eyes arrive.
        floorFeedback("warn");
        // A tap on a feed that has GIVEN UP is the retry. While one is in
        // flight it is not: invalidating a running query only restarts the
        // answer the operator is already waiting for.
        if (grant === "unreachable") {
          void queryClient.invalidateQueries({ queryKey: AUTH_TENANTS_QUERY_KEY });
        }
      }}
    >
      <span className="floor-tile-title">{title}</span>
      <span className="floor-tile-sub">
        {checking ? (
          <>
            <span className="skeleton floor-tile-bar" aria-hidden="true" />
            {t("inventory.floor.home.unavailableChecking")}
          </>
        ) : (
          t("inventory.floor.home.unavailableUnknown")
        )}
      </span>
      {/* The fix offered AT the control (D-118), before the tap rather than
          after it — an operator who has just walked back into signal has
          something to do about it. */}
      {!checking && !asked ? (
        <span className="floor-tile-hint">{t("inventory.floor.home.unavailableRetryHint")}</span>
      ) : null}
      {/* THE ANSWER. `role="status"` so the tap is answered to a screen reader
          too, and inside the slab so it cannot be scrolled away from the
          control it belongs to. */}
      {asked ? (
        <span className="floor-tile-answer" role="status" data-testid={`${testId}-answer`}>
          {checking
            ? t("inventory.floor.home.unavailableWait")
            : t("inventory.floor.home.unavailableRetrying")}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The module-07 tile (slice 07-A). It needs its OWN derivation rather than
 * `grantOf(perms, …)`: `useInventoryPermissions` resolves `inventory.*` codes
 * from the role feed and would answer `false` to every `quality.*` code —
 * which is fail-closed in the worst way, silently hiding the tile from the qc
 * lead it exists for. The role FEED is shared (one fetch, one retry policy);
 * only the grant table differs, so this reads the same feed through module
 * 07's own table.
 *
 * It renders through the SAME `GatedTile` as its neighbours (task #115): it
 * used to carry its own copy of the inert shape, which is how one tile's dead
 * `<p>` became four.
 */
function QualityTile({
  perms,
}: {
  perms: RoleSignal & { isResolved: boolean; isFeedError: boolean };
}) {
  const { t } = useTranslation();
  const codes = deriveQualityPermissions(perms);
  return (
    <GatedTile
      grant={tileGrant(perms, codes.has("quality.inspection.read"))}
      to="/floor/quality"
      testId="tile-quality"
      title={t("quality.floor.queueTitle")}
      sub={t("quality.floor.emptyBody")}
    />
  );
}

/**
 * The module-09 tile (spec 09 §4.8, P3-D PR-D3, D-286): the ship queue and
 * the pick screen behind it. Its OWN derivation for the same reason the
 * quality tile has one — `useInventoryPermissions` answers `false` to every
 * `sales.*` code — read through module 09's own mirror
 * (`deriveSalesPermissions`, areas/office/sales/hooks/permissions.ts) on
 * `sales.shipment.post`: {group_admin, facility_admin, storekeeper} hold it,
 * and the storekeeper's facility narrowing is the SERVICE's (D-224), never a
 * missing grant here. The same `GatedTile` as its neighbours, so a cold
 * offline start leaves it in place, inert and answering a tap (D-118, task
 * #115), never a `<p>` and never an absence the operator reads as a refusal.
 */
function ShipTile({
  perms,
}: {
  perms: RoleSignal & { isResolved: boolean; isFeedError: boolean };
}) {
  const { t } = useTranslation();
  const codes = deriveSalesPermissions(perms);
  return (
    <GatedTile
      grant={tileGrant(perms, codes.has("sales.shipment.post"))}
      to="/floor/ship"
      testId="tile-ship"
      title={t("sales.floor.ship.tile")}
      sub={t("sales.floor.ship.tileSub")}
    />
  );
}

/**
 * "Next for you" (D-444 ruling 2): the one thing this worker should open now,
 * on top of the hub, so nine equal tiles are no longer the first thing a
 * gloved hand has to choose between. It reads only what the device already
 * holds (the outbox, the pinned station, the role feed), so it is the same
 * with no signal and costs the hub no request.
 *
 * In order: records the office could not take (they need the worker before
 * anything else), the station this tablet was last pinned to, choosing a
 * station for a worker who books production, then the first task the role
 * holds. A role with none of these gets no card.
 */
export type NextForYou =
  | { readonly kind: "tray"; readonly count: number }
  | { readonly kind: "station"; readonly workCenterId: string; readonly name: string | undefined }
  | { readonly kind: "pickStation" }
  | { readonly kind: "counts" }
  | { readonly kind: "receive" }
  | { readonly kind: "inspect" }
  | { readonly kind: "ship" };

/** Pure: the one next thing, from what the device knows (unit-tested). */
export function nextForYou(input: {
  readonly attentionCount: number;
  readonly pinned:
    { readonly workCenterId: string; readonly name?: string | undefined } | undefined;
  readonly can: {
    readonly station: boolean;
    readonly counts: boolean;
    readonly receive: boolean;
    readonly inspect: boolean;
    readonly ship: boolean;
  };
}): NextForYou | undefined {
  if (input.attentionCount > 0) return { kind: "tray", count: input.attentionCount };
  if (input.can.station && input.pinned !== undefined) {
    return { kind: "station", workCenterId: input.pinned.workCenterId, name: input.pinned.name };
  }
  if (input.can.station) return { kind: "pickStation" };
  if (input.can.counts) return { kind: "counts" };
  if (input.can.receive) return { kind: "receive" };
  if (input.can.inspect) return { kind: "inspect" };
  if (input.can.ship) return { kind: "ship" };
  return undefined;
}

function NextForYouCard({ next }: { next: NextForYou }) {
  const { t } = useTranslation();
  const label = <span className="floor-next-label">{t("home.floor.nextLabel")}</span>;
  const body = (title: string, sub: string) => (
    <>
      {label}
      <span className="floor-next-title">{title}</span>
      <span className="floor-next-sub">{sub}</span>
    </>
  );
  switch (next.kind) {
    case "tray":
      return (
        <Link
          to="/floor/tray"
          className="floor-next floor-next-alert"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(
            t("inventory.floor.sync.attention", { count: next.count }),
            t("home.floor.nextTraySub"),
          )}
        </Link>
      );
    case "station":
      return (
        <Link
          to="/floor/station/$workCenterId"
          params={{ workCenterId: next.workCenterId }}
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(
            next.name !== undefined && next.name.length > 0
              ? t("home.floor.nextStation", { name: next.name })
              : t("home.floor.nextStationUnnamed"),
            t("home.floor.nextStationSub"),
          )}
        </Link>
      );
    case "pickStation":
      return (
        <Link
          to="/floor/station"
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(t("home.floor.nextPickStation"), t("home.floor.nextPickStationSub"))}
        </Link>
      );
    case "counts":
      return (
        <Link
          to="/floor/counts"
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(t("inventory.floor.home.counts"), t("home.floor.nextCountsSub"))}
        </Link>
      );
    case "receive":
      return (
        <Link
          to="/floor/receive"
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(t("inventory.floor.home.receive"), t("home.floor.nextReceiveSub"))}
        </Link>
      );
    case "inspect":
      return (
        <Link
          to="/floor/quality"
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(t("quality.floor.queueTitle"), t("home.floor.nextInspectSub"))}
        </Link>
      );
    case "ship":
      return (
        <Link
          to="/floor/ship"
          className="floor-next"
          data-testid="floor-next"
          data-next={next.kind}
        >
          {body(t("sales.floor.ship.tile"), t("home.floor.nextShipSub"))}
        </Link>
      );
  }
}

/**
 * A module-04 tile (the station picker, my shift). These stay LIVE while the
 * role feed has no answer, because a cold offline start must still open the
 * cached tally at shift end (the note on the pair below), and they are absent
 * only once the feed has ANSWERED that the role books no production (D-444
 * ruling 4): a storekeeper's hub has no "My station".
 */
function ProductionTile({
  refused,
  to,
  testId,
  title,
  sub,
}: {
  refused: boolean;
  to: "/floor/station" | "/floor/my-shift";
  testId: string;
  title: string;
  sub: string;
}) {
  if (refused) return null;
  return (
    <Link to={to} className="floor-tile" data-testid={testId}>
      <span className="floor-tile-title">{title}</span>
      <span className="floor-tile-sub">{sub}</span>
    </Link>
  );
}

export function FloorHomeScreen() {
  const { t, i18n } = useTranslation();
  const perms = useInventoryPermissions();
  const production = useProductionPermissions();
  const outbox = useFloorOutbox();

  // Resolved and refused, the only state that removes a tile (D-118).
  const resolved = perms.isResolved && !perms.isFeedError;
  const booksProduction = production.has("production.floor.record");
  const productionRefused = resolved && production.isResolved && !booksProduction;
  const quality = deriveQualityPermissions(perms);
  const sales = deriveSalesPermissions(perms);
  const next = resolved
    ? nextForYou({
        attentionCount: outbox.attentionCount,
        pinned: pinnedStation(),
        can: {
          station: booksProduction,
          counts: perms.has("inventory.count.perform"),
          receive: perms.has("inventory.transfer.receive"),
          inspect: quality.has("quality.inspection.record"),
          ship: sales.has("sales.shipment.post"),
        },
      })
    : outbox.attentionCount > 0
      ? ({ kind: "tray", count: outbox.attentionCount } as const)
      : undefined;

  return (
    <FloorScreen title={t("inventory.floor.home.title")} wide>
      {next !== undefined ? <NextForYouCard next={next} /> : null}
      <nav className="floor-tiles" aria-label={t("inventory.floor.home.title")}>
        <GatedTile
          grant={grantOf(perms, "inventory.scan.locate")}
          to="/floor/scan"
          testId="tile-scan"
          title={t("inventory.floor.home.scan")}
          sub={t("inventory.floor.home.scanSub")}
        />

        <Link to="/floor/stock" className="floor-tile" data-testid="tile-stock">
          <span className="floor-tile-title">{t("inventory.floor.home.stock")}</span>
          <span className="floor-tile-sub">{t("inventory.floor.home.stockSub")}</span>
        </Link>

        <GatedTile
          grant={grantOf(perms, "inventory.transfer.receive")}
          to="/floor/receive"
          testId="tile-receive"
          title={t("inventory.floor.home.receive")}
          sub={t("inventory.floor.home.receiveSub")}
        />

        <GatedTile
          grant={grantOf(perms, "inventory.count.perform")}
          to="/floor/counts"
          testId="tile-counts"
          title={t("inventory.floor.home.counts")}
          sub={t("inventory.floor.home.countsSub")}
        />

        {/* Module 09 §4.8 floor (P3-D PR-D3, D-286): the ship queue. Placed
            after the module-06 stock flow and before quality — the storekeeper
            who picks is the same person who counts, and the pick follows the
            put-away in the day's order. Gated on `sales.shipment.post` through
            the SALES mirror (see ShipTile); the same three states as every
            gated tile. */}
        <ShipTile perms={perms} />

        {/* Module 07 §4.3 floor (slice 07-A). REACHABILITY IS PART OF DONE: the
            inspection queue is the only way onto the execution screen from a
            tablet, and a screen with no way in is not shipped. Gated on the
            §7 read cell through the SAME `tileGrant` rule AND the same
            `GatedTile` rendering as its neighbours — an unresolved feed leaves
            the tile in place, visibly not-live and answering a tap, because a
            cold offline start must not read as a refusal (D-118) and must not
            read as a live tile either (task #115). */}
        <QualityTile perms={perms} />

        <Link
          to="/floor/tray"
          className={`floor-tile ${outbox.attentionCount > 0 ? "floor-tile-alert" : ""}`}
          data-testid="tile-tray"
        >
          <span className="floor-tile-title">{t("inventory.floor.home.tray")}</span>
          <span className="floor-tile-sub">
            {outbox.attentionCount > 0
              ? t("inventory.floor.sync.attention", { count: outbox.attentionCount })
              : t("inventory.floor.home.traySub")}
          </span>
        </Link>

        {/* ---- Module 04's two hub entries: S9 station + S11 my-shift --------
            WHY THESE EXIST (operator-shift audit, 2026-08-17): /floor/station
            and /floor/my-shift were mounted, specced and unit-tested — and
            REACHABLE ONLY BY typed URL or a /q/wc QR deep link. This hub — the
            screen whose own header note says "a screen with no way in is not
            shipped" — offered no tile for either, so an operator WITHOUT a QR
            in hand could not claim a station (the picker exists for exactly
            that case: router.tsx, the 04-B-3 block), and an operator ending a
            shift could not open their own tally at all. Every e2e reached both
            routes with page.goto, which is why no walkthrough ever noticed.

            UNGATED ON PURPOSE, like Stock and the Tray: neither destination
            has a §7 cell to mirror — e16 aggregates the SESSION principal
            (my-shift takes no id, so there is nothing to be refused), and the
            station queue is a read every floor role holds. Wrapping these in
            GatedTile would re-create the D-118 failure this file's header
            documents: a cold offline start would hide my-shift — the one
            screen whose cached tally exists to be read offline at shift end.

            ORDER: appended after the six existing tiles, before the quiet
            product-card lookup. The module-06 flow keeps its muscle-memory
            positions 1–6 (including the alert-capable Tray); the grid has no
            fixed capacity — it is "a wall of destinations" (floor-inventory
            .css) that scrolls, one column on a phone and two from 640 px — so
            two more tiles cost scroll depth, never layout, and on a tablet
            the module-04 pair lands together as its own row. */}
        <ProductionTile
          refused={productionRefused}
          to="/floor/station"
          testId="tile-station"
          title={t("production.floor.home.station")}
          sub={t("production.floor.home.stationSub")}
        />

        <ProductionTile
          refused={productionRefused}
          to="/floor/my-shift"
          testId="tile-my-shift"
          title={t("production.floor.myShift.title")}
          sub={t("production.floor.home.myShiftSub")}
        />

        <Link to="/floor/lookup" className="floor-tile floor-tile-quiet" data-testid="tile-product">
          <span className="floor-tile-title">{t("inventory.floor.home.productCard")}</span>
          <span className="floor-tile-sub">{t("inventory.floor.home.productCardSub")}</span>
        </Link>

        {/* The customer guide's floor section: a plain link, because /guide is
            served by the API behind the same session (guide §7). */}
        <a
          href={guideHref(i18n.language, "floor")}
          className="floor-tile floor-tile-quiet"
          data-testid="tile-help"
        >
          <span className="floor-tile-title">{t("nav.helpTile")}</span>
          <span className="floor-tile-sub">{t("nav.helpTileSub")}</span>
        </a>
      </nav>
    </FloorScreen>
  );
}
